import React, { useState, useRef } from 'react';
import { Upload, X, FileText, CheckCircle, Loader } from 'lucide-react';
import { screeningAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const NewScreening = ({ onSuccess, onError }) => {
  const { user } = useAuth();
  const [jobTitle, setJobTitle] = useState('');
  const [jobDescFile, setJobDescFile] = useState(null);
  const [resumeFiles, setResumeFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const jobDescInputRef = useRef(null);
  const resumeInputRef = useRef(null);

  // Get max resumes allowed based on user's subscription plan
  // FREE: 5, PROFESSIONAL: 20, BUSINESS: 50, ENTERPRISE: 100
  const maxResumes = user?.maxResumesPerJob || 5;

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e, type) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = Array.from(e.dataTransfer.files);
    if (type === 'job') {
      handleJobDescUpload({ target: { files: [files[0]] } });
    } else {
      handleResumesUpload({ target: { files } });
    }
  };

  const handleJobDescUpload = (e) => {
    const file = e.target.files[0];
    if (file && (file.type === 'application/pdf' || file.name.endsWith('.docx'))) {
      setJobDescFile(file);
      onError('');
    } else {
      onError('Please upload a PDF or DOCX file for job description');
    }
  };

  const handleResumesUpload = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(f =>
      f.type === 'application/pdf' || f.name.endsWith('.docx')
    );

    if (validFiles.length !== files.length) {
      onError('Some files were skipped. Only PDF and DOCX files are allowed.');
    }

    if (resumeFiles.length + validFiles.length > maxResumes) {
      onError(`Your ${user?.subscriptionPlan || 'FREE'} plan allows maximum ${maxResumes} resumes per screening`);
      return;
    }

    setResumeFiles([...resumeFiles, ...validFiles]);
  };

  const removeResume = (index) => {
    setResumeFiles(resumeFiles.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!jobDescFile) {
      onError('Please upload a job description');
      return;
    }

    if (resumeFiles.length === 0) {
      onError('Please upload at least one resume');
      return;
    }

    if (user.credits < 1) {
      onError('Insufficient credits. Please upgrade your plan.');
      return;
    }

    setLoading(true);
    setUploadProgress(0);
    onError('');

    // Simulate progress
    const progressInterval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return 90;
        }
        return prev + 10;
      });
    }, 500);

    try {
      const formData = new FormData();
      formData.append('jobTitle', jobTitle);
      formData.append('jobDescription', jobDescFile);
      resumeFiles.forEach(file => {
        formData.append('resumes', file);
      });

      const response = await screeningAPI.create(formData);

      setUploadProgress(100);
      clearInterval(progressInterval);

      // Reset form
      setJobTitle('');
      setJobDescFile(null);
      setResumeFiles([]);

      setTimeout(() => {
        onSuccess(response.data);
        setLoading(false);
        setUploadProgress(0);
      }, 500);
    } catch (err) {
      clearInterval(progressInterval);
      onError(err.response?.data?.message || 'Screening failed. Please try again.');
      setLoading(false);
      setUploadProgress(0);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-8 mb-8 border border-gray-200">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center">
          <FileText className="w-5 h-5 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">New Screening Job</h2>
      </div>

      <div className="space-y-6">
        {/* Job Title */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Job Title <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            placeholder="e.g., Senior Java Developer"
            value={jobTitle}
            onChange={(e) => setJobTitle(e.target.value)}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-purple-500 transition"
          />
        </div>

        {/* Job Description Upload */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Job Description <span className="text-red-500">*</span>
          </label>
          <div
            className={`relative border-2 border-dashed rounded-xl p-8 text-center transition cursor-pointer ${
              dragActive
                ? 'border-purple-500 bg-purple-50'
                : jobDescFile
                ? 'border-green-500 bg-green-50'
                : 'border-gray-300 hover:border-purple-400 bg-gray-50'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={(e) => handleDrop(e, 'job')}
            onClick={() => jobDescInputRef.current?.click()}
          >
            {jobDescFile ? (
              <div className="flex items-center justify-center gap-3">
                <CheckCircle className="w-6 h-6 text-green-600" />
                <span className="text-green-700 font-medium">{jobDescFile.name}</span>
              </div>
            ) : (
              <>
                <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600 font-medium mb-1">Drop your job description here</p>
                <p className="text-sm text-gray-500">or click to browse (PDF, DOCX)</p>
              </>
            )}
            <input
              ref={jobDescInputRef}
              type="file"
              accept=".pdf,.docx"
              onChange={handleJobDescUpload}
              className="hidden"
            />
          </div>
        </div>

        {/* Resumes Upload */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Candidate Resumes <span className="text-red-500">*</span>
            <span className="text-gray-500 font-normal ml-2">(Max {maxResumes} files)</span>
          </label>
          <div
            className={`relative border-2 border-dashed rounded-xl p-8 text-center transition cursor-pointer ${
              dragActive
                ? 'border-purple-500 bg-purple-50'
                : 'border-gray-300 hover:border-purple-400 bg-gray-50'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={(e) => handleDrop(e, 'resumes')}
            onClick={() => resumeInputRef.current?.click()}
          >
            <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600 font-medium mb-1">Drop resumes here or click to browse</p>
            <p className="text-sm text-gray-500 mb-2">PDF or DOCX files only</p>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg">
              <FileText className="w-4 h-4 text-purple-600" />
              <span className="text-sm font-medium text-gray-700">{resumeFiles.length}/{maxResumes} uploaded</span>
            </div>
            <input
              ref={resumeInputRef}
              type="file"
              accept=".pdf,.docx"
              multiple
              onChange={handleResumesUpload}
              className="hidden"
            />
          </div>

          {resumeFiles.length > 0 && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              {resumeFiles.map((file, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-purple-300 transition">
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-purple-600" />
                    <span className="text-sm text-gray-700 truncate">{file.name}</span>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeResume(index);
                    }}
                    className="text-red-500 hover:text-red-700 transition"
                  >
                    <X size={18} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Progress Bar */}
        {loading && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600 font-medium">Processing with AI...</span>
              <span className="text-purple-600 font-bold">{uploadProgress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-blue-600 to-purple-600 rounded-full transition-all duration-500"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
            <p className="text-xs text-gray-500 text-center">This usually takes 30-60 seconds...</p>
          </div>
        )}

        {/* Submit Button */}
        <button
          onClick={handleSubmit}
          disabled={loading || !jobDescFile || resumeFiles.length === 0 || !jobTitle}
          className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-4 rounded-xl font-bold text-lg hover:shadow-xl transform hover:-translate-y-0.5 transition disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <Loader className="w-5 h-5 animate-spin" />
              Processing... Please wait
            </>
          ) : (
            <>
              <CheckCircle className="w-5 h-5" />
              Start AI Screening (1 Credit)
            </>
          )}
        </button>

        {/* Info */}
        <div className="flex items-center justify-center gap-6 text-sm text-gray-500">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-green-500 rounded-full" />
            <span>Usually completes in 60 seconds</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-blue-500 rounded-full" />
            <span>Powered by Google Gemini AI</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewScreening;